import Vr360ViewerModal from 'components/desktop/shared/vr360-viewer-modal';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPhoto,
  IconVR,
  IconVideo,
  IconVideo360,
} from 'components/icons';
import ImageViewer from 'components/shared/image-viewer';
import ModalWrapper from 'components/shared/modal-wrapper';
import PanoramaViewer from 'components/shared/paranoma-viewer';
import VideoPlayer from 'components/shared/video-player';
import { FileWithFullUrls, MediaUnionWithFullUrls, YoutubeFile } from 'graphql/main/queries';
import { useModal, useTranslation } from 'hooks';
import { filter, includes, isEmpty, isEqual, isNil, map, toString } from 'lodash';
import { DetailProjectPageContext } from 'pages/du-an/[chi-tiet-du-an]';
import { useContext, useEffect, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

const Gallery = () => {
  const { project } = useContext(DetailProjectPageContext).pageData ?? {};
  const { project: projectDetail } = project ?? {};
  const { mediaUrls, projectStatus, vr360Urls, panoramicImages } = projectDetail ?? {};
  const translation = useTranslation();
  const { showing: showingImageViewerModal, toggle: toggleImageViewerModal } = useModal();
  const { showing: showingVideoPlayerModal, toggle: toggleVideoPlayerModal } = useModal();
  const { showing: showingPanoramaModal, toggle: togglePanoramaModal } = useModal();
  const { showing: showingVr360Modal, toggle: toggleVr360Modal } = useModal();
  const [medias, setMedias] = useState<MediaUnionWithFullUrls[]>();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentItem, setCurrentItem] = useState<MediaUnionWithFullUrls | undefined>(medias?.[0]);
  const [currentFilter, setCurrentFilter] = useState<string[]>(['image']);
  const filters = [
    {
      icon: <IconPhoto className='min-h-[24px] min-w-[24px]' />,
      types: ['image'],
    },
    {
      icon: <IconVideo className='min-h-[24px] min-w-[24px]' />,
      types: ['video', 'youtube'],
    },
    {
      icon: <IconVideo360 className='min-h-[24px] min-w-[24px]' />,
      types: ['image360'],
    },
  ];
  const filteredMedias = filter(medias, (media) =>
    includes(isEqual(currentFilter, ['image360']) ? ['image'] : currentFilter, media.type),
  );

  const selectMedia = (media: MediaUnionWithFullUrls, mediaIndex: number) => {
    setCurrentItem(media);
    setCurrentIndex(mediaIndex);
  };
  const filterMedia = (types: string[]) => {
    if (!isEqual(currentFilter, types)) {
      setCurrentFilter(types);
      if (isEqual(['image360'], types)) {
        setMedias(filter(panoramicImages, (media) => includes(['image'], media.type)));
      } else setMedias(filter(mediaUrls, (media) => includes(types, media.type)));
    }
  };
  const view = () => {
    if (!currentItem) return;
    if (isEqual(currentFilter, ['image'])) {
      toggleImageViewerModal();
    }
    if (isEqual(currentFilter, ['video', 'youtube'])) {
      toggleVideoPlayerModal();
    }
    if (isEqual(currentFilter, ['image360'])) {
      togglePanoramaModal();
    }
  };

  useEffect(() => {
    setMedias(mediaUrls);
  }, [mediaUrls]);
  useEffect(() => {
    setCurrentItem(medias?.[0]);
  }, [medias]);

  return (
    <div
      id='gallery'
      className='container mx-auto flex min-w-[1052px] flex-col space-x-[24px] px-[54px] screen-1140:px-[98px] xl:px-[48px] screen-1366:px-[83px]'
    >
      <div className='flex flex-col overflow-hidden rounded-[16px] bg-paper shadow-1'>
        <button type='button' className='relative h-[500px] w-full' onClick={view}>
          <img
            src={
              currentItem
                ? includes(['image', 'image360'], currentItem.type)
                  ? (currentItem as FileWithFullUrls).originalUrl ?? '/images/error-image.svg'
                  : isEqual(currentItem.type, 'youtube')
                  ? (currentItem as YoutubeFile).youtubeMetadata?.thumbnailUrl ??
                    '/images/video-thumbnail.webp'
                  : isEqual(currentItem.type, 'video')
                  ? filter((currentItem as FileWithFullUrls).urls, (url) =>
                      isEqual(url.type, 'thumbnailHd'),
                    )?.[0]?.url ?? '/images/video-thumbnail.webp'
                  : '/images/video-thumbnail.webp'
                : '/images/error-image.svg'
            }
            alt='current-gallery'
            loading='lazy'
            className='h-full w-full object-cover'
          />
          {!isNil(projectStatus) && (
            <div className='absolute left-[20px] top-[22px] overflow-hidden rounded-[4px] bg-paper'>
              <div
                className={`w-fit px-[12px] py-[7px] ${
                  isEqual(projectStatus, 'onSale')
                    ? 'bg-[#6356fe26]'
                    : isEqual(projectStatus, 'constructingFoundation')
                    ? 'bg-[#e0656526]'
                    : isEqual(projectStatus, 'completedFiftyPercent')
                    ? 'bg-[#dd992a26]'
                    : isEqual(projectStatus, 'roofed')
                    ? 'bg-[#3e6cb026]'
                    : ''
                }`}
              >
                <span
                  className={`font-[600] ${
                    isEqual(projectStatus, 'onSale')
                      ? 'text-[#6356fe]'
                      : isEqual(projectStatus, 'constructingFoundation')
                      ? 'text-[#e06565]'
                      : isEqual(projectStatus, 'completedFiftyPercent')
                      ? 'text-[#dd992a]'
                      : isEqual(projectStatus, 'roofed')
                      ? 'text-[#3e6cb0]'
                      : ''
                  }`}
                >
                  {(translation.major.projectStatus as any)[toString(projectStatus)]}
                </span>
              </div>
            </div>
          )}
        </button>
        <ModalWrapper showing={showingImageViewerModal}>
          <ImageViewer
            activeIndex={currentIndex}
            images={filteredMedias}
            onClose={toggleImageViewerModal}
          />
        </ModalWrapper>
        <ModalWrapper showing={showingVideoPlayerModal}>
          <VideoPlayer
            activeIndex={currentIndex}
            videos={filteredMedias}
            onClose={toggleVideoPlayerModal}
          />
        </ModalWrapper>
        <ModalWrapper showing={showingPanoramaModal}>
          <PanoramaViewer
            activeIndex={currentIndex}
            images={filteredMedias}
            onClose={togglePanoramaModal}
          />
        </ModalWrapper>
        <div className='flex items-center space-x-[32px] px-[16px] py-[12px] xl:max-w-[800px]'>
          <div className='flex w-full space-x-[20px] overflow-hidden'>
            <button id='gallery-swiper-back' type='button' className='disabled:opacity-50'>
              <IconChevronLeft className='min-h-[24px] min-w-[24px]' />
            </button>
            <div className='w-full overflow-hidden'>
              <Swiper
                threshold={10}
                draggable
                slidesPerView='auto'
                spaceBetween={16}
                navigation={{ prevEl: '#gallery-swiper-back', nextEl: '#gallery-swiper-forward' }}
                modules={[Navigation]}
              >
                {map(filteredMedias, (media, mediaIndex) => (
                  <SwiperSlide
                    key={`${media.fileId}-${mediaIndex}`}
                    className='min-w-[139px] max-w-[139px] xl:min-w-[86.75px] xl:max-w-[86.75px]'
                  >
                    <div
                      className={`h-[60px] cursor-pointer overflow-hidden rounded-[8px] ${
                        isEqual(currentItem?.fileId, media.fileId) ? 'border-2 border-stroke' : ''
                      }`}
                      onClick={() => {
                        selectMedia(media, mediaIndex);
                      }}
                    >
                      <img
                        src={
                          media
                            ? includes(['image', 'image360'], media.type)
                              ? (media as FileWithFullUrls).originalUrl ?? '/images/error-image.svg'
                              : isEqual(media.type, 'youtube')
                              ? (media as YoutubeFile).youtubeMetadata?.thumbnailUrl ??
                                '/images/video-thumbnail.webp'
                              : isEqual(currentItem?.type, 'video')
                              ? filter((currentItem as FileWithFullUrls)?.urls, (url) =>
                                  isEqual(url.type, 'thumbnailHd'),
                                )?.[0]?.url ?? '/images/video-thumbnail.webp'
                              : '/images/video-thumbnail.webp'
                            : '/images/error-image.svg'
                        }
                        alt={`gallery-${mediaIndex}`}
                        loading='lazy'
                        className='h-full w-full object-cover'
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <button id='gallery-swiper-forward' type='button' className='disabled:opacity-50'>
              <IconChevronRight className='min-h-[24px] min-w-[24px]' />
            </button>
          </div>
          <div className='flex items-center'>
            <div className='flex items-center justify-around space-x-[20px] p-[20px]'>
              {map(filters, (filterItem, filterItemIndex) => (
                <button
                  key={filterItemIndex}
                  type='button'
                  disabled={isEmpty(
                    isEqual(filterItem.types, ['image360'])
                      ? filter(panoramicImages, (media) => includes(['image'], media.type))
                      : filter(mediaUrls, (media) => includes(filterItem.types, media.type)),
                  )}
                  className={`flex h-[36px] w-[36px] items-center justify-center rounded-[4px] disabled:opacity-50 disabled:hover:bg-tertiary ${
                    isEqual(currentFilter, filterItem.types)
                      ? 'cursor-default border border-primary bg-secondary text-primary'
                      : 'bg-tertiary text-text2 hover:bg-tertiary-light'
                  }`}
                  onClick={() => {
                    filterMedia(filterItem.types);
                  }}
                >
                  {filterItem.icon}
                </button>
              ))}
            </div>
            <div className='border-l border-stroke p-[20px]'>
              <button
                type='button'
                disabled={isEmpty(vr360Urls)}
                className='flex h-[36px] w-[36px] items-center justify-center rounded-[6px] bg-primary transition duration-[200ms] ease-in-out hover:bg-primary-light disabled:opacity-50'
                onClick={toggleVr360Modal}
              >
                <IconVR className='min-h-[24px] min-w-[24px] text-paper' />
              </button>
              <ModalWrapper isClickAway showing={showingVr360Modal} onToggle={toggleVr360Modal}>
                <Vr360ViewerModal vrLinks={vr360Urls} onClose={toggleVr360Modal} />
              </ModalWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gallery;
